/**
 * The Project lifecycle checklist, arranged under the workspace stages, with the steps that
 * have a real record behind them ticked from that record. Those steps can't disagree with the
 * Board: a paid deposit invoice ticks "Deposit received", a booking ticks "Schedule
 * Installation Date", and so on. Steps with no record behind them stay manual, and Complete
 * Project is always a deliberate click because it closes the workspace.
 */
import type { WorkflowTask } from '../../components/ProjectWorkflow';
import { normalizeInvoiceRole } from '../../modules/docs/utils/invoiceLedger';
import { toDateOnly } from '../../modules/docs/utils/invoicePaymentTerms';
import { isLiveIssuedInvoice, type OperationalFacts, type OperationalQueueId } from './operationalState';
import type { OpsDocument } from './workspaceDashboard.model';

/** Task name → where the tick comes from, e.g. "Deposit invoice KSU-0005-01 paid 3 Oct 2026". */
export type RecordTicks = Record<string, string>;

function shortDate(value?: string | null): string {
  const day = toDateOnly(value);
  if (!day) return '';
  const [year, month, date] = day.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, date, 12)).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Europe/London',
  });
}

function isPaid(doc: OpsDocument): boolean {
  const status = String(doc.status || '').toLowerCase();
  return status === 'paid' || status === 'cash';
}

function invoiceName(doc: OpsDocument, fallback: string): string {
  const ref = String(doc.invoice_ref || '').trim();
  return ref ? `${fallback} ${ref}` : fallback;
}

function withDate(text: string, value?: string | null): string {
  const date = shortDate(value);
  return date ? `${text} ${date}` : text;
}

/** Records outside the documents that back a step; each is the time it happened. */
export type RecordStamps = {
  /** Present (possibly without a time) once the Final Survey is marked complete. */
  finalSurveyCompletedAt?: string | null;
  /** Every job card still carrying the job is marked complete. */
  jobCardsCompletedAt?: string | null;
  reviewRequestedAt?: string | null;
};

function isSentQuote(doc: OpsDocument): boolean {
  if (doc.kind !== 'Quotation' && doc.kind !== 'Estimate') return false;
  const status = String(doc.status || '').toLowerCase();
  return !!(doc.sent_at || doc.accepted_at) || ['sent', 'accepted', 'invoiced', 'paid', 'cash'].includes(status);
}

export function deriveRecordTicks(facts: OperationalFacts, documents: OpsDocument[], stamps: RecordStamps = {}): RecordTicks {
  const ticks: RecordTicks = {};
  // A quote can only go to the customer once the first survey is done
  const sentQuote = documents
    .filter(isSentQuote)
    .sort((a, b) => String(a.sent_at || a.created_at || '').localeCompare(String(b.sent_at || b.created_at || '')))[0];
  if (sentQuote) {
    ticks['First Survey Complete'] = withDate(`${sentQuote.kind} sent`, sentQuote.sent_at || sentQuote.accepted_at || sentQuote.created_at);
  }
  if (stamps.finalSurveyCompletedAt != null) {
    ticks['Final Survey Complete'] = withDate('Final Survey marked complete', stamps.finalSurveyCompletedAt);
  }
  if (facts.productionDate) {
    ticks['Order Goods'] = withDate('Production date', facts.productionDate);
  }
  if (stamps.jobCardsCompletedAt) {
    ticks[facts.supplyOnly ? 'Delivery Complete' : 'Installation Complete'] = withDate('Job cards complete', stamps.jobCardsCompletedAt);
  }
  if (stamps.reviewRequestedAt) {
    ticks['Request Review'] = withDate('Review request sent', stamps.reviewRequestedAt);
  }
  const deposit = facts.depositInvoice;
  if (facts.depositPaid && deposit) {
    ticks['Deposit received'] = withDate(`${invoiceName(deposit, 'Deposit invoice')} paid`, facts.depositPaidAt || deposit.paid_at);
  }
  if (facts.bookingDate) {
    const task = facts.supplyOnly ? 'Schedule Delivery Date' : 'Schedule Installation Date';
    ticks[task] = withDate(facts.supplyOnly ? 'Delivery booked for' : 'Installation booked for', facts.bookingDate);
  }
  const works = documents.filter((doc) => doc.kind === 'Invoice'
    && String(doc.status || '').toLowerCase() !== 'void'
    && normalizeInvoiceRole(doc.invoice_role) === 'works'
    && isLiveIssuedInvoice(doc));
  const issued = works[0];
  if (issued) {
    ticks['Request payment'] = withDate(`${invoiceName(issued, 'Balance invoice')} issued`, issued.issued_at || issued.sent_at || issued.created_at);
  }
  const paid = works.find(isPaid);
  if (paid) {
    ticks['Receive payment'] = withDate(`${invoiceName(paid, 'Balance invoice')} paid`, paid.paid_at);
  }
  return ticks;
}

/** Stored ticks with the record-backed ones applied; record-backed steps are always ticked. */
export function applyRecordTicks(tasks: WorkflowTask[], ticks: RecordTicks | null | undefined): WorkflowTask[] {
  if (!ticks || !Object.keys(ticks).length) return tasks;
  return tasks.map((task) => (ticks[task.task] && !task.completed ? { ...task, completed: true } : task));
}

/** Checklist steps under each workspace stage. Cancel Project is shown separately. */
export function checklistByStage(supplyOnly: boolean): Array<{ stage: OperationalQueueId; tasks: string[] }> {
  return [
    { stage: 'presale', tasks: ['First Survey Complete', 'Supplier Quote Received'] },
    { stage: 'deposit', tasks: ['Deposit received'] },
    { stage: 'ordering', tasks: ['Final Survey Complete', 'Order Goods'] },
    {
      stage: 'installation',
      tasks: supplyOnly
        ? ['Schedule Delivery Date', 'Delivery Complete']
        : ['Schedule Installation Date', 'Installation Complete'],
    },
    { stage: 'aftercare', tasks: ['Request payment', 'Receive payment', 'Send Receipt', 'Request Review', 'Complete Project'] },
  ];
}
